import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getLibraryMaterials, setLibraryLocation, bulkSetLibraryLocation, getLocationMaster, addLocationValue, deleteLocationValue } from '../services/api';
import { toast } from 'sonner';
import { Boxes, MapPin, Search, Package, Layers, Trash2, Plus } from 'lucide-react';
import PickingTab from '../components/warehouse/PickingTab';
import MovementTab from '../components/warehouse/MovementTab';

const emptyLoc = { warehouse: 'Main', zone: '', aisle: '', rack: '', shelf: '', bin: '' };
const locLabel = (loc) => loc ? [loc.warehouse, loc.zone, loc.aisle, loc.rack, loc.shelf, loc.bin].filter(Boolean).join(' · ') : null;

// Cascade chain top -> bottom, each level's options are filtered by the value chosen at its parent level
const CHAIN = ['warehouse', 'zone', 'aisle', 'rack', 'shelf', 'bin'];
const filterByParent = (list, parentVal) => (list || []).filter((o) => !o.parent || o.parent === parentVal);

const FIELD_DEFS = [
  { key: 'warehouses', label: 'Warehouses', cap: false, single: 'warehouse', parentKey: null, parentLabel: null },
  { key: 'zones', label: 'Zones', cap: false, single: 'zone', parentKey: 'warehouses', parentLabel: 'Warehouse' },
  { key: 'aisles', label: 'Aisles', cap: false, single: 'aisle', parentKey: 'zones', parentLabel: 'Zone' },
  { key: 'racks', label: 'Racks', cap: true, single: 'rack', parentKey: 'aisles', parentLabel: 'Aisle' },
  { key: 'shelves', label: 'Shelves', cap: false, single: 'shelf', parentKey: 'racks', parentLabel: 'Rack' },
  { key: 'bins', label: 'Bins', cap: true, single: 'bin', parentKey: 'shelves', parentLabel: 'Shelf' },
];

function LSelect({ label, options, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          {(options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.value}{o.capacity ? ` (cap ${o.capacity})` : ''}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function LocationForm({ master, loc, setLoc }) {
  // Setting a level clears everything below it so the cascade stays consistent
  const set = (f, v) => {
    const idx = CHAIN.indexOf(f);
    const next = { ...loc, [f]: v };
    for (let i = idx + 1; i < CHAIN.length; i++) next[CHAIN[i]] = '';
    setLoc(next);
  };
  const zoneOpts = filterByParent(master?.zones, loc.warehouse);
  const aisleOpts = filterByParent(master?.aisles, loc.zone);
  const rackOpts = filterByParent(master?.racks, loc.aisle);
  const shelfOpts = filterByParent(master?.shelves, loc.rack);
  const binOpts = filterByParent(master?.bins, loc.shelf);
  return (
    <div className="grid grid-cols-2 gap-3">
      <LSelect label="Warehouse" options={master?.warehouses} value={loc.warehouse} onChange={(v) => set('warehouse', v)} testid="loc-warehouse" />
      <LSelect label="Zone" options={zoneOpts} value={loc.zone} onChange={(v) => set('zone', v)} testid="loc-zone" />
      <LSelect label="Aisle" options={aisleOpts} value={loc.aisle} onChange={(v) => set('aisle', v)} testid="loc-aisle" />
      <LSelect label="Rack" options={rackOpts} value={loc.rack} onChange={(v) => set('rack', v)} testid="loc-rack" />
      <LSelect label="Shelf" options={shelfOpts} value={loc.shelf} onChange={(v) => set('shelf', v)} testid="loc-shelf" />
      <LSelect label="Bin" options={binOpts} value={loc.bin} onChange={(v) => set('bin', v)} testid="loc-bin" />
    </div>
  );
}

const WarehousingPage = () => {
  const [materials, setMaterials] = useState([]);
  const [master, setMaster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [loc, setLoc] = useState(emptyLoc);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoc, setBulkLoc] = useState(emptyLoc);

  // Location master add-forms
  const [newVals, setNewVals] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [m, lm] = await Promise.all([getLibraryMaterials(), getLocationMaster()]);
      setMaterials(m.data);
      setMaster(lm.data);
    } catch (e) {
      toast.error('Failed to load warehousing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAssign = (m) => { setActive(m); setLoc(m.location || emptyLoc); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      const res = await setLibraryLocation(active.id, loc);
      toast.success('Stacking location saved');
      if (res.data?.capacity_warning) toast.warning(res.data.capacity_warning);
      setDialogOpen(false); setActive(null); fetchData();
    } catch (e) { toast.error('Failed to save location'); }
  };

  const toggleSelect = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleBulkSave = async () => {
    if (selected.length === 0) return;
    try {
      const res = await bulkSetLibraryLocation(selected, bulkLoc);
      toast.success(`Placed ${res.data.updated} material(s)`);
      if (res.data?.capacity_warning) toast.warning(res.data.capacity_warning);
      setBulkOpen(false); setSelected([]); fetchData();
    } catch (e) { toast.error('Bulk placement failed'); }
  };

  const addVal = async (field) => {
    const v = newVals[field];
    if (!v || !v.value) { toast.error('Enter a value'); return; }
    try {
      const res = await addLocationValue(field, v.value, v.capacity ? parseFloat(v.capacity) : null, v.parent || null);
      setMaster(res.data);
      setNewVals({ ...newVals, [field]: { value: '', capacity: '', parent: '' } });
      toast.success('Added');
    } catch (e) { toast.error('Failed to add'); }
  };

  const delVal = async (field, value) => {
    try { const res = await deleteLocationValue(field, value); setMaster(res.data); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const filtered = materials.filter((m) =>
    m.item_name.toLowerCase().includes(search.toLowerCase()) ||
    (m.item_code || '').toLowerCase().includes(search.toLowerCase())
  );
  const placed = materials.filter((m) => m.location);
  const unplaced = materials.filter((m) => !m.location);

  const board = {};
  for (const m of placed) {
    const wh = m.location.warehouse || 'Main';
    const zone = m.location.zone || 'Unzoned';
    board[wh] = board[wh] || {};
    board[wh][zone] = board[wh][zone] || [];
    board[wh][zone].push(m);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="warehousing-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Warehousing</h1>
        <p className="text-muted-foreground">Plan and place material stacking locations from the Material Library</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center"><Boxes size={20} className="text-blue-600" /></div><div><div className="text-2xl font-bold">{materials.length}</div><div className="text-sm text-muted-foreground">Library Items</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-green-50 flex items-center justify-center"><MapPin size={20} className="text-green-600" /></div><div><div className="text-2xl font-bold">{placed.length}</div><div className="text-sm text-muted-foreground">Placed</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-yellow-50 flex items-center justify-center"><Package size={20} className="text-yellow-600" /></div><div><div className="text-2xl font-bold">{unplaced.length}</div><div className="text-sm text-muted-foreground">Unplaced</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-purple-50 flex items-center justify-center"><Layers size={20} className="text-purple-600" /></div><div><div className="text-2xl font-bold">{Object.keys(board).length}</div><div className="text-sm text-muted-foreground">Warehouses</div></div></CardContent></Card>
      </div>

      <Tabs defaultValue="library">
        <TabsList>
          <TabsTrigger value="library" data-testid="library-tab">Material Library</TabsTrigger>
          <TabsTrigger value="board" data-testid="board-tab">Stacking Board</TabsTrigger>
          <TabsTrigger value="picking" data-testid="picking-tab">Picking Lists</TabsTrigger>
          <TabsTrigger value="movements" data-testid="movements-tab">Movement History</TabsTrigger>
          <TabsTrigger value="locations" data-testid="locations-tab">Location Master</TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="mt-4 space-y-4">
          <Card><CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input placeholder="Search materials..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="library-search" />
            </div>
            {selected.length > 0 && (
              <Button className="btn-accent w-full sm:w-auto" onClick={() => { setBulkLoc(emptyLoc); setBulkOpen(true); }} data-testid="bulk-assign-btn">
                <MapPin size={16} className="mr-1" />Assign Selected ({selected.length})
              </Button>
            )}
          </CardContent></Card>
          <Card><CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" /></div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-muted-foreground"><Boxes size={40} className="mb-3 opacity-50" /><p>No library materials yet</p><p className="text-sm mt-1">Receive items against a PI in Procurement to populate this library</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50"><tr>
                    <th className="px-4 py-3 w-10"></th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Code</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Item</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Available</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Total Len (m)</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Value (₹)</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Taxable (₹)</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Action</th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {filtered.map((m) => (
                      <tr key={m.id} className="hover:bg-muted/30" data-testid={`library-row-${m.id}`}>
                        <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggleSelect(m.id)} data-testid={`select-${m.id}`} /></td>
                        <td className="px-4 py-3 font-mono text-sm">{m.item_code || '-'}</td>
                        <td className="px-4 py-3"><div>{m.item_name}</div>{(m.finish || m.material_type) && <div className="text-xs text-muted-foreground">{[m.finish, m.material_type].filter(Boolean).join(' · ')}</div>}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${m.item_type === 'profile' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{m.item_type || '-'}</span></td>
                        <td className="px-4 py-3 font-mono">{m.available_quantity} {m.unit}</td>
                        <td className="px-4 py-3 font-mono text-sm">{m.item_type === 'profile' ? (m.total_length_m || 0) : '-'}</td>
                        <td className="px-4 py-3 font-mono text-sm">{(m.value || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 font-mono text-sm">{(m.taxable_value || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-sm">{m.location ? <span className="text-blue-600 flex items-center gap-1"><MapPin size={14} />{locLabel(m.location)}</span> : <span className="text-muted-foreground">Not placed</span>}</td>
                        <td className="px-4 py-3"><Button variant="outline" size="sm" onClick={() => openAssign(m)} data-testid={`assign-location-${m.id}`}><MapPin size={14} className="mr-1" />{m.location ? 'Edit' : 'Assign'} Location</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="board" className="mt-4">
          {placed.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No materials placed yet. Assign locations in the Material Library tab.</p>
          ) : (
            <div className="space-y-6">
              {Object.entries(board).map(([wh, zones]) => (
                <div key={wh}>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2"><Boxes size={18} className="text-accent" />{wh}</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(zones).map(([zone, items]) => (
                      <Card key={zone} data-testid={`board-zone-${wh}-${zone}`}>
                        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Layers size={14} />Zone: {zone}</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                          {items.map((m) => (
                            <div key={m.id} className="flex justify-between items-center text-sm border-l-2 border-accent pl-2 py-1">
                              <div><p className="font-medium">{m.item_name}</p><p className="text-xs text-muted-foreground">{locLabel(m.location)}</p></div>
                              <span className="font-mono text-xs">{m.available_quantity} {m.unit}</span>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="picking" className="mt-4">
          <PickingTab />
        </TabsContent>

        <TabsContent value="movements" className="mt-4">
          <MovementTab />
        </TabsContent>

        <TabsContent value="locations" className="mt-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FIELD_DEFS.map((fd) => (
              <Card key={fd.key} data-testid={`master-${fd.key}`}>
                <CardHeader className="pb-2"><CardTitle className="text-sm">{fd.label}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {(master?.[fd.key] || []).map((o) => (
                      <div key={o.value} className="flex items-center justify-between text-sm bg-muted/40 rounded px-2 py-1">
                        <span>{o.value}{o.capacity ? <span className="text-xs text-muted-foreground"> · cap {o.capacity}</span> : ''}{o.parent ? <span className="text-xs text-blue-600"> ← {o.parent}</span> : ''}</span>
                        <button onClick={() => delVal(fd.key, o.value)} className="text-muted-foreground hover:text-red-600" data-testid={`del-${fd.key}-${o.value}`}><Trash2 size={14} /></button>
                      </div>
                    ))}
                    {(master?.[fd.key] || []).length === 0 && <p className="text-xs text-muted-foreground">No values yet</p>}
                  </div>
                  <div className="space-y-2 pt-1">
                    {fd.parentKey && (
                      <Select value={newVals[fd.key]?.parent || ''} onValueChange={(v) => setNewVals({ ...newVals, [fd.key]: { ...newVals[fd.key], parent: v } })}>
                        <SelectTrigger className="h-8" data-testid={`new-${fd.key}-parent`}><SelectValue placeholder={`Belongs to ${fd.parentLabel}`} /></SelectTrigger>
                        <SelectContent>
                          {(master?.[fd.parentKey] || []).map((p) => <SelectItem key={p.value} value={p.value}>{p.value}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    <div className="flex gap-2">
                      <Input placeholder="New value" value={newVals[fd.key]?.value || ''} onChange={(e) => setNewVals({ ...newVals, [fd.key]: { ...newVals[fd.key], value: e.target.value } })} className="h-8" data-testid={`new-${fd.key}-value`} />
                      {fd.cap && <Input type="number" placeholder="Cap" value={newVals[fd.key]?.capacity || ''} onChange={(e) => setNewVals({ ...newVals, [fd.key]: { ...newVals[fd.key], capacity: e.target.value } })} className="h-8 w-20" data-testid={`new-${fd.key}-cap`} />}
                      <Button size="sm" variant="outline" onClick={() => addVal(fd.key)} data-testid={`add-${fd.key}-btn`}><Plus size={14} /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Single assign */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Stacking Location — {active?.item_name}</DialogTitle></DialogHeader>
          <LocationForm master={master} loc={loc} setLoc={setLoc} />
          <Button className="w-full btn-accent mt-2" onClick={handleSave} data-testid="save-location-btn">Save Location</Button>
        </DialogContent>
      </Dialog>

      {/* Bulk assign */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Place {selected.length} Material(s)</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">All selected materials will be assigned to this location.</p>
          <LocationForm master={master} loc={bulkLoc} setLoc={setBulkLoc} />
          <Button className="w-full btn-accent mt-2" onClick={handleBulkSave} data-testid="bulk-save-btn">Place Materials</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehousingPage;
